import { buildNcidQrImageUrl } from "../../utils/ncidClient";
import "./NpidDashboardSection.css";

const DEMO_NCID = "NCID-26-CA-00001";
const DEMO_CATEGORY = "CA · Care Attendant";

const FEATURES = [
  {
    id: "identity",
    icon: "shield-check",
    title: "NCID: Digital Identity Verification on NEMA CareHub",
    body: "NCID provides identity verification for care professionals on NEMA CareHub.",
  },
];

const VERIFY_STEPS = [
  "Scan the QR code on the care professional's profile",
  "Review the identity-verification status shown on the NCID profile",
  "Make your hiring decision directly with the professional",
];

function ShieldIcon({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function NpidMark({ className = "" }) {
  return (
    <svg className={className} viewBox="0 0 40 40" fill="none" aria-hidden>
      <path
        d="M20 2.5 34 8v10.5c0 9.2-6.2 15.2-14 17.5C12.2 33.7 6 27.7 6 18.5V8l14-5.5z"
        fill="currentColor"
      />
      <path
        d="M14.2 27V13.2h3.1l5.2 9.4V13.2H26V27h-3.1l-5.2-9.4V27H14.2z"
        fill="#fff"
      />
    </svg>
  );
}

function FeatureIcon({ name }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.75",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  };

  if (name === "chart") {
    return (
      <svg {...common}>
        <path d="M4 19V5" />
        <path d="M4 19h16" />
        <path d="M8 15v-3" />
        <path d="M12 15V9" />
        <path d="M16 15v-6" />
        <path d="M14 7l3-3 3 3" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function UserBadgeIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M5 19.5v-1a5.5 5.5 0 0 1 5.5-5.5h3A5.5 5.5 0 0 1 19 18.5v1"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <path
        d="M17.5 7.5 19 9l2.5-2.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UserPlusIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="1.75" />
      <path d="M19 8v6M16 11h6" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M3 8h10M9.5 4.5 13 8l-3.5 3.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DemoQrCard() {
  const verifyUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/caregivers/1?verify=${encodeURIComponent(DEMO_NCID)}`
      : `https://nemacare.in/caregivers/1?verify=${encodeURIComponent(DEMO_NCID)}`;
  const qrSrc = buildNcidQrImageUrl(verifyUrl, 136);

  return (
    <div className="npid-dash__qr-card">
      <div className="npid-dash__qr-head">Scan to Verify NCID</div>
      <div className="npid-dash__qr-body">
        <div className="npid-dash__qr-frame">
          {qrSrc ? (
            <img src={qrSrc} alt="" width={136} height={136} loading="lazy" />
          ) : (
            <div className="npid-dash__qr-fallback" aria-hidden />
          )}
          <span className="npid-dash__qr-mark" aria-hidden>
            <NpidMark />
          </span>
        </div>
        <strong className="npid-dash__qr-code">{DEMO_NCID}</strong>
        <span className="npid-dash__qr-role">{DEMO_CATEGORY}</span>
      </div>
    </div>
  );
}

export default function NpidDashboardSection({ onApply }) {
  return (
    <section className="npid-dash" aria-labelledby="npid-dash-heading">
      <div className="npid-dash__grid">
        <div className="npid-dash__intro">
          <span className="npid-dash__eyebrow">
            <ShieldIcon />
            DIGITAL TRUST ARCHITECTURE
          </span>

          <div className="npid-dash__title-row">
            <NpidMark className="npid-dash__title-mark" />
            <h2 id="npid-dash-heading" className="npid-dash__heading">
              NCID: The NEMA CareHub Identity
            </h2>
          </div>

          <p className="npid-dash__body">
            <strong className="npid-dash__em">NCID</strong> is the identity-verification feature
            associated with a professional&apos;s profile on NEMA CareHub.
          </p>
          <p className="npid-dash__body">
            Families can review the identity-verification status shown on a professional&apos;s
            profile before making a hiring decision. NCID does not verify qualifications,
            background clearance or clinical competence.
          </p>

          <ul className="npid-dash__features">
            {FEATURES.map((item) => (
              <li key={item.id} className="npid-dash__feature">
                <span className="npid-dash__feature-icon" aria-hidden>
                  <FeatureIcon name={item.icon} />
                </span>
                <div>
                  <strong className="npid-dash__feature-title">{item.title}</strong>
                  <p className="npid-dash__feature-body">{item.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <aside className="npid-dash__verify" aria-label="How verification works">
          <h3 className="npid-dash__verify-title">How Verification Works</h3>
          <ol className="npid-dash__steps">
            {VERIFY_STEPS.map((step, index) => (
              <li key={step} className="npid-dash__step">
                <span className="npid-dash__step-num" aria-hidden>
                  {index + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
          <DemoQrCard />
        </aside>
      </div>

      <aside className="npid-dash__cta" aria-label="Join as a caregiver">
        <span className="npid-dash__cta-icon" aria-hidden>
          <UserBadgeIcon />
        </span>
        <div className="npid-dash__cta-copy">
          <h3 className="npid-dash__cta-title">
            Are you a Home Caregiver, Nurse, or Therapist in India?
          </h3>
          <p className="npid-dash__cta-body">
            Join our marketplace of care professionals and start earning up to ₹40k – ₹70k/month.
          </p>
        </div>
        <button type="button" className="npid-dash__cta-btn" onClick={onApply}>
          <UserPlusIcon />
          <span>Build My NCID — Apply Free</span>
          <ArrowIcon />
        </button>
      </aside>
    </section>
  );
}
